import { useEffect, useId, useRef, useState } from "preact/hooks";
import { formatRemaining } from "../lib/duration";
import { dismissDailyLimit, me } from "../state";

/**
 * Shown when a model-backed question is rejected because the rolling 24h
 * allowance is spent. Reads `me` for the limit and counts down to the next
 * question.
 *
 * Deliberately has no call to action: the judge is free and there is nothing to
 * buy, so the only way forward is waiting for the window to roll (cached
 * answers stay available in the meantime). Mounted once at the app root so it
 * works from any route and for both new questions and follow-ups.
 */
export function DailyLimitDialog() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  // Refresh the countdown while the dialog is open (minutes resolution).
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const nextAvailableAt = me.value?.nextAvailableAt ?? null;
  const dailyLimit = me.value?.dailyLimit;
  const target = nextAvailableAt ? new Date(nextAvailableAt).getTime() : null;
  const remaining = target != null ? target - now : null;
  const countdown = remaining == null
    ? null
    : remaining <= 0
    ? "any moment now"
    : formatRemaining(remaining);

  return (
    <dialog
      ref={dialogRef}
      class="confirm"
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Intercept the native close so we own the state.
        event.preventDefault();
        dismissDailyLimit();
      }}
      onClick={(event) => {
        // Clicking the backdrop (the dialog element itself) dismisses.
        if (event.target === dialogRef.current) dismissDailyLimit();
      }}
    >
      <div class="confirm__panel">
        <h2 class="confirm__title" id={titleId}>
          You have reached the daily question limit
        </h2>
        <p class="confirm__body">
          {dailyLimit
            ? `You get ${dailyLimit} ${
              dailyLimit === 1 ? "question" : "questions"
            } every 24 hours. `
            : "There's a limit on questions every 24 hours. "}
          {countdown
            ? `Your next one unlocks in ${countdown}.`
            : "Your next one unlocks soon."}
          <br />
          Answers already in the cache stay available in the meantime.
        </p>
        <div class="confirm__actions">
          <button
            type="button"
            class="btn btn--ghost"
            onClick={dismissDailyLimit}
          >
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}
